
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import styles from "../styles/DashboardStyles/Dashboard.module.css";

import { DashboardOutlined, BookOutlined, ReadOutlined, GiftOutlined, DollarOutlined, CreditCardOutlined, TransactionOutlined, LineChartOutlined, TeamOutlined, FolderOutlined, ScheduleOutlined, FileTextOutlined, UsergroupAddOutlined, UserOutlined, AppstoreOutlined, QuestionCircleOutlined, HomeOutlined, GlobalOutlined, SettingOutlined, InfoCircleOutlined, PictureOutlined, StarOutlined } from '@ant-design/icons';

import { useAuth } from "../provider/Auth.provider";
import SidebarRouting from "../components/FlowComponents/Sidebar/SidebarRouting";
import Header from "../Flows/SuperAdmin/Dashboard/Header";
import UserNotification from "../components/FlowComponents/userNotification/UserNotification";
import UserProfile from "../components/FlowComponents/userProfile/UserProfile";
import { findUserByUID } from "../service/findUsers/findUserByUID";
import { sampleNotificationData } from "../service/notificationService/sampleNotificationData";

const SuperAdminLayout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userData, setUserData] = useState({});
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      // Check if user or user.uid is not available
      if (!user || !user.uid) {
        navigate("/superadmin/signin");
        return;
      }

      try {
        const userData = await findUserByUID(user.uid);
        // Check if userData exists and user_type is superAdmin
        if (!userData || userData.userTypes !== 'super_admin') {
          navigate("/superadmin/signin");
          return;
        } else {
          // Only redirect to dashboard if we're not already on a valid superadmin page
          const currentPath = window.location.pathname;
          if (currentPath === "/superadmin" || currentPath === "/superadmin/") {
            navigate("/superadmin/dashboard", { replace: true });
          }
        }
        setUserData(userData);
      } catch (error) {
        console.error("Error fetching user data:", error);
        navigate("/superadmin/signin");
      }
    };
    fetchUserData();
  }, [user, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMenuExpanded(false);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const menuItems = [
    { key: "dashboard", label: "Dash", icon: <DashboardOutlined />, path: "/superadmin/dashboard" },
    {
      key: "lms",
      label: "LMS",
      icon: <ReadOutlined />,
      children: [
        { key: "package", label: "Package", icon: <FolderOutlined />, path: "/superadmin/lms/package" },
        { key: "batch", label: "Batch", icon: <ScheduleOutlined />, path: "/superadmin/lms/batch" },
        { key: "courses", label: "Courses", icon: <BookOutlined />, path: "/superadmin/lms/courses" },
        { key: "module", label: "Module", icon: <FileTextOutlined />, path: "/superadmin/lms/module" },
      ],
    },
    { key: "gift", label: "Gift", icon: <GiftOutlined />, path: "/superadmin/gift" },
    {
      key: "payout",
      label: "Payout",
      icon: <DollarOutlined />,
      children: [
        {
          key: "club-trainer",
          label: "Club Trainer",
          icon: <UserOutlined />,
          path: "/superadmin/payout/club-trainer"
        },
        { key: "club-admin", label: "Club Admin", icon: <UserOutlined />, path: "/superadmin/payout/club-admin" },
        { key: "club-leader", label: "Club Leader", icon: <UserOutlined />, path: "/superadmin/payout/club-leader" },
        { key: "club-member", label: "Club Member", icon: <UsergroupAddOutlined />, path: "/superadmin/payout/club-member" },
      ],
    },
    { key: "payments", label: "Payments", icon: <CreditCardOutlined />, path: "/superadmin/payments" },
    { key: "transactions", label: "Transactions", icon: <TransactionOutlined />, path: "/superadmin/transactions" },
    { key: "sales", label: "Sales", icon: <LineChartOutlined />, path: "/superadmin/sales" },
    { key: "leader", label: "Leader", icon: <TeamOutlined />, path: "/superadmin/leader" },
    {
      key: "cms",
      label: "CMS",
      icon: <AppstoreOutlined />,
      children: [
        {
          key: "landing",
          label: "Landing Page",
          icon: <HomeOutlined />,
          children: [
            { key: "faq", label: "FAQ", icon: <QuestionCircleOutlined />, path: "/superadmin/cms/landing/faq" },
            { key: "reviews", label: "Google Reviews", icon: <StarOutlined />, path: "/superadmin/cms/landing/reviews" },

            { key: "proofs", label: "Proofs", icon: <PictureOutlined />, path: "/superadmin/cms/landing/proofs" },
            { key: "trainers", label: "Trainers", icon: <UserOutlined />, path: "/superadmin/cms/landing/trainers" },
            // Add more landing page items here
          ]
        },
        {
          key: "footer",
          label: "Footer",
          icon: <GlobalOutlined />,
          children: [
            { key: "disclaimer", label: "Disclaimer", icon: <FileTextOutlined />, path: "/superadmin/cms/footer/disclaimer" },
            { key: "contactus", label: "Contact Us", icon: <FileTextOutlined />, path: "/superadmin/cms/footer/contactus" },
            { key: "terms", label: "Terms and Conditions", icon: <FileTextOutlined />, path: "/superadmin/cms/footer/terms" },
            { key: "refundpolicy", label: "Refund Policy", icon: <FileTextOutlined />, path: "/superadmin/cms/footer/refundpolicy" },
            { key: "miscellaneous", label: "Miscellaneous", icon: <FileTextOutlined />, path: "/superadmin/cms/footer/miscellaneous" },
          ]
        },
        // Add more CMS sections here
      ],
    },
  ];

  return (
    <div className={styles.dashboard}>
      <SidebarRouting
        menuItems={menuItems}
      />
      <div className={styles.maincontent}>
        <Header
          userData={userData}
        />
        <Outlet />
      </div>
    </div>
  );
};

export default SuperAdminLayout;
